import { useCallback, useEffect, useRef } from "react";

import { Moment } from "moment";

import { CropCardRefMap } from "pages/app/all-field-view/components/AllFieldsTimeline";

const SCROLL_CONFIG = {
  maxAttempts: 60,
  intervalMs: 80,
  initialDelayMs: 50,
  scrollViewportFraction: 0.7,
  scrollResetThreshold: 100,
  maxScrollRetries: 10,
} as const;

export type MissingDataTarget = {
  fieldId: string;
  itemId: string;
  startTime: Moment;
};

type UseAutoScrollToMissingDataProps = {
  enabled: boolean;
  target: MissingDataTarget | null;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  reportingYear: number;
  getCropCardRefMap: () => CropCardRefMap;
  handleShowMissingDataYear: (fieldId: string, itemId: string) => void;
  ensureFieldInView: (fieldId: string) => void;
  onComplete: () => void;
};

const createRefKey = (fieldId: string, itemId: string): string => JSON.stringify({ fieldId, itemId });

const getVisibleYearsInViewport = (scrollContainer: HTMLDivElement): number[] => {
  const yearHeaderElements = document.querySelectorAll<HTMLDivElement>(".header-years.content");
  const scrollRect = scrollContainer.getBoundingClientRect();
  const yearRegex = /\b(\d{4})\b/; // regex to match 4-digit year (I guess there could be a better way to do this? but it works haha)

  return Array.from(yearHeaderElements)
    .map((el) => {
      const match = yearRegex.exec(el.textContent ?? "");
      if (!match) return null;

      const year = Number(match[1]);
      const rect = el.getBoundingClientRect();
      const isVisible = rect.right > scrollRect.left && rect.left < scrollRect.right;

      return isVisible ? year : null;
    })
    .filter((year): year is number => year !== null);
};

const calculateScrollDirection = (targetYear: number, visibleYears: number[], fallbackYear: number): -1 | 0 | 1 => {
  const minVisible = visibleYears.length ? Math.min(...visibleYears) : fallbackYear;
  const maxVisible = visibleYears.length ? Math.max(...visibleYears) : fallbackYear;

  if (targetYear < minVisible) return -1;
  if (targetYear > maxVisible) return 1;
  return 0; // target is visible - yay! soilMates we rock!!! :D
};

export const useAutoScrollToMissingData = ({
  enabled,
  target,
  scrollRef,
  reportingYear,
  getCropCardRefMap,
  handleShowMissingDataYear,
  ensureFieldInView,
  onComplete,
}: UseAutoScrollToMissingDataProps): void => {
  const scrollStateRef = useRef<{
    applied: boolean;
    position: number | null;
    retryCount: number;
  }>({ applied: false, position: null, retryCount: 0 });

  const resetScrollState = useCallback(() => {
    scrollStateRef.current = { applied: false, position: null, retryCount: 0 };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (!target) {
      onComplete();
      return;
    }

    const { fieldId, itemId, startTime } = target;
    const targetYear = startTime.year();
    const refKey = createRefKey(fieldId, itemId);

    // Prepare the row to render
    ensureFieldInView(fieldId);

    // Reset vertical scroll
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }

    resetScrollState();

    let cancelled = false;
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const attemptNavigation = () => {
      if (cancelled) return;
      attempts += 1;

      const scrollContainer = scrollRef.current;
      const node = getCropCardRefMap().get(refKey);

      // Node found && connected: prolly gonna apply or verify the scroll
      if (node?.isConnected && scrollContainer) {
        const state = scrollStateRef.current;

        if (state.applied && state.position !== null) {
          const wasReset = Math.abs(scrollContainer.scrollLeft - state.position) > SCROLL_CONFIG.scrollResetThreshold;

          if (wasReset && state.retryCount < SCROLL_CONFIG.maxScrollRetries) {
            // Re-apply scroll that was reset by timeline
            handleShowMissingDataYear(fieldId, itemId);
            scrollStateRef.current.position = scrollContainer.scrollLeft;
            scrollStateRef.current.retryCount += 1;
            timeoutId = setTimeout(attemptNavigation, SCROLL_CONFIG.intervalMs);
            return;
          }

          // Scroll held or max retries reached - done
          onComplete();
          return;
        }

        // First successful scroll
        handleShowMissingDataYear(fieldId, itemId);
        scrollStateRef.current = {
          applied: true,
          position: scrollContainer.scrollLeft,
          retryCount: 0,
        };
        timeoutId = setTimeout(attemptNavigation, SCROLL_CONFIG.intervalMs);
        return;
      }

      // Node not rendered: progressively scroll toward target year
      if (scrollContainer) {
        const visibleYears = getVisibleYearsInViewport(scrollContainer);
        const direction = calculateScrollDirection(targetYear, visibleYears, reportingYear);

        if (direction !== 0) {
          const scrollAmount = scrollContainer.clientWidth * SCROLL_CONFIG.scrollViewportFraction;
          scrollContainer.scrollBy({ left: direction * scrollAmount });
        }
      }

      // Give up after max attempts
      if (attempts >= SCROLL_CONFIG.maxAttempts) {
        onComplete();
        return;
      }

      timeoutId = setTimeout(attemptNavigation, SCROLL_CONFIG.intervalMs);
    };

    timeoutId = setTimeout(attemptNavigation, SCROLL_CONFIG.initialDelayMs);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [enabled, target, scrollRef, reportingYear, getCropCardRefMap, handleShowMissingDataYear, ensureFieldInView, onComplete, resetScrollState]);
};
